import type { BuildingRules } from './building.js';
import type { ElevatorSpec, FloorIndex, PassengerTraits, StarThresholds } from './types.js';
import { DEFAULT_TRAITS } from './types.js';
import type { Lift, Loadout } from './upgrades.js';
import { BASE_LOADOUT, STANDARD_LIFT, stockSpec } from './upgrades.js';

export type { StarThresholds } from './types.js';

/** One scripted passenger arrival. v0.1 levels are fully authored, not generated. */
export interface SpawnDef {
  readonly atMs: number;
  readonly origin: FloorIndex;
  readonly destination: FloorIndex;
  /** Name of an archetype from ARCHETYPES. Omit for an ordinary passenger. */
  readonly archetype?: string;
  /**
   * Per-spawn overrides applied on top of the archetype. Use sparingly — a level
   * that leans on these is describing an archetype that should have a name.
   */
  readonly traits?: Partial<Omit<PassengerTraits, 'archetype'>>;
  /** Ties this spawn to others in the same level; they board all-or-nothing. */
  readonly group?: string;
  /**
   * Appear `atMs` after another spawn of this level — by its place in the list,
   * which must come first — is delivered, instead of at a set time (ADR-0029).
   * How a call-out's return is written.
   */
  readonly follows?: number;
  /**
   * Floors to stop at on the way, with them aboard, before they get out at
   * their destination (ADR-0031): a postman's rounds.
   */
  readonly stops?: readonly FloorIndex[];
}

/**
 * The v0.2 rule vocabulary, as named bundles of traits.
 *
 * Five, deliberately. The plan update lists roughly fifteen possible archetypes
 * and then says not to introduce them all at once; this is the controlled
 * onboarding curve. Each one exists for the conflict it creates, not the icon.
 */
export const ARCHETYPES: Readonly<Record<string, PassengerTraits>> = {
  /** The baseline everyone is measured against. */
  normal: { ...DEFAULT_TRAITS },

  /** Urgency. Serve them soon or lose them — and losing them costs the score. */
  impatient: { ...DEFAULT_TRAITS, archetype: 'impatient', patienceMs: 18_000 },

  /** Spatial trade-off. Two slots means taking one costs you somebody else. */
  luggage: { ...DEFAULT_TRAITS, archetype: 'luggage', size: 2 },

  /** Route conflict. A deadline that pulls the car away from the efficient sweep. */
  courier: { ...DEFAULT_TRAITS, archetype: 'courier', deadlineMs: 30_000 },

  /** Value conflict. Worth serving fast, rarely convenient to serve fast. */
  vip: { ...DEFAULT_TRAITS, archetype: 'vip', vip: true, patienceMs: 35_000 },

  /**
   * Emergencies (ADR-0029): a crew up to an incident and back down with who
   * they came for, on one clock, and the run ends if it runs out. Two crews,
   * two different conflicts on the way back.
   *
   * Space: the paramedics come down with a stretcher that takes three seats.
   */
  paramedic: {
    ...DEFAULT_TRAITS,
    archetype: 'paramedic',
    critical: true,
    withinMs: 65_000,
    callout: { back: 'stretcher', stayMs: 4_000 },
  },
  stretcher: { ...DEFAULT_TRAITS, archetype: 'stretcher', size: 3, critical: true },

  /** The lift itself: the police come down with someone under arrest, and ride alone. */
  police: {
    ...DEFAULT_TRAITS,
    archetype: 'police',
    critical: true,
    withinMs: 75_000,
    callout: { back: 'arrest', stayMs: 3_000 },
  },
  arrest: { ...DEFAULT_TRAITS, archetype: 'arrest', size: 2, critical: true, alone: true },

  /**
   * Each building's own people (ADR-0031): one each, and each makes its
   * building's rule bite somewhere new.
   *
   * The Office Tower's postman: a mail cart that takes two seats, and a round
   * of floors to stop at before getting off. Free on a route that passes them,
   * costly on one that does not. Their floors come with the spawn (`stops`).
   */
  postman: {
    ...DEFAULT_TRAITS,
    archetype: 'postman',
    size: 2,
    patienceMs: 40_000,
    staff: true,
  },

  /**
   * The Grand Hotel's housekeeping: a cart that takes two places on a corridor
   * that holds three, and is never turned away from it. Moving her a floor
   * frees the corridor for the guests she would otherwise turn away.
   */
  housekeeping: {
    ...DEFAULT_TRAITS,
    archetype: 'housekeeping',
    size: 2,
    landingSpace: 2,
    staff: true,
  },

  /**
   * The Transit Centre's commuters, down to a train that leaves at a set time:
   * the level's departure, written into each one's deadline by the traffic that
   * sends them. Whoever is not on the platform by then has missed it.
   */
  commuter: { ...DEFAULT_TRAITS, archetype: 'commuter' },

  /**
   * The Skyline Tower's window cleaner: up to the roof with two seats of kit,
   * through the sky lobby, a while at work, and back down the same way. A
   * call-out that is not an emergency — losing them costs points, not the shift.
   */
  windowCleaner: {
    ...DEFAULT_TRAITS,
    archetype: 'windowCleaner',
    size: 2,
    patienceMs: 45_000,
    staff: true,
    callout: { back: 'windowCleanerDown', stayMs: 8_000 },
  },
  windowCleanerDown: {
    ...DEFAULT_TRAITS,
    archetype: 'windowCleanerDown',
    size: 2,
    patienceMs: 45_000,
    staff: true,
  },

  /**
   * More of each building's own people (ADR-0043), for buildings of forty
   * shifts. Each is a bundle of rules the ticket already shows, in a pairing no
   * one else has.
   *
   * The Grand Hotel's room service: a trolley that takes two seats, and a
   * dinner going cold on it. Space and a deadline at once, where a courier
   * travels light.
   */
  roomService: {
    ...DEFAULT_TRAITS,
    archetype: 'roomService',
    size: 2,
    deadlineMs: 40_000,
    staff: true,
  },

  /**
   * The Grand Hotel's bellhop: a trolley of bags that takes three seats, left
   * at every floor on his round before he is done. The postman's round in a car
   * he half fills. His floors come with the spawn (`stops`).
   */
  bellhop: {
    ...DEFAULT_TRAITS,
    archetype: 'bellhop',
    size: 3,
    patienceMs: 45_000,
    staff: true,
  },

  /**
   * The Grand Hotel's departures (ADR-0051): like the Transit Centre's trains,
   * one time for everyone running for it, written into each one's deadline by
   * the traffic that sends them (`traffic.trains`).
   *
   * Guests for the airport shuttle, down to the lobby with a suitcase that takes
   * two seats: a full car and a shared departure at once.
   */
  shuttleGuest: { ...DEFAULT_TRAITS, archetype: 'shuttleGuest', size: 2 },
  /** Guests for breakfast, up to the restaurant on the top floor before it closes. */
  breakfastGuest: { ...DEFAULT_TRAITS, archetype: 'breakfastGuest' },
  /** A couple to be married, who ride together, down to the car to the ceremony. */
  newlywed: { ...DEFAULT_TRAITS, archetype: 'newlywed' },

  /** The Transit Centre's cyclist: a bike that takes two seats, and no patience to spare. */
  cyclist: { ...DEFAULT_TRAITS, archetype: 'cyclist', size: 2, patienceMs: 20_000 },

  /**
   * The Skyline Tower's chief executive, a bodyguard at their side: worth
   * double, draining twice as fast, and two seats to find for it.
   */
  ceo: { ...DEFAULT_TRAITS, archetype: 'ceo', vip: true, size: 2, patienceMs: 35_000 },

  /**
   * The Skyline Tower's investor: worth double and draining twice as fast,
   * with a meeting to reach before the bar runs out. Value and a deadline at
   * once, where a VIP's clock is only their patience.
   */
  investor: {
    ...DEFAULT_TRAITS,
    archetype: 'investor',
    vip: true,
    patienceMs: null,
    deadlineMs: 45_000,
  },
};

export function traitsFor(spawn: SpawnDef): PassengerTraits {
  const base = spawn.archetype ? ARCHETYPES[spawn.archetype] : DEFAULT_TRAITS;
  if (!base) {
    throw new Error(
      `Unknown archetype "${spawn.archetype}". Known: ${Object.keys(ARCHETYPES).join(', ')}`,
    );
  }
  return {
    ...base,
    ...spawn.traits,
    ...(spawn.group === undefined ? {} : { groupId: spawn.group }),
    archetype: spawn.archetype ?? base.archetype,
  };
}

export type Objective =
  /** Deliver every passenger the level spawns. */
  | { readonly kind: 'deliverAll' }
  /** Deliver at least N. Lets a level spawn more demand than the player must serve. */
  | { readonly kind: 'deliverN'; readonly count: number }
  /**
   * Deliver N *and* finish with the service score above a floor.
   *
   * The roadmap's "maintain rating": moving people is no longer enough on its
   * own, so a run that hits the quota by letting everyone else stew still fails.
   */
  | { readonly kind: 'serviceAbove'; readonly count: number; readonly minService: number }
  /**
   * Last out a rush without losing more than N people.
   *
   * A different shape from the others: the clock is the goal rather than the
   * limit, so the question stops being "how fast" and becomes "how long can you
   * hold this".
   */
  | { readonly kind: 'survive'; readonly untilMs: number; readonly maxLost: number }
  /**
   * Everyone home within N trips (ADR-0037): a puzzle, with no clock to beat.
   * A trip is each time a car sets off for a floor; the one past the limit
   * ends the shift, and so does running out with someone still to take.
   */
  | { readonly kind: 'withinTrips'; readonly trips: number };

/**
 * A shift's one goal beyond winning it (ADR-0040), optional for the player.
 * Each asks for a run done well rather than a lift bought well, so a strong
 * lift does not make it automatic.
 */
export type BonusGoal =
  /** Nobody walks off, misses their deadline or is turned away. */
  | { readonly kind: 'noneLost' }
  /** Everyone with a deadline delivered before it: couriers, commuters for a train. */
  | { readonly kind: 'couriersOnTime' }
  /** Every VIP delivered. */
  | { readonly kind: 'allVips' }
  /** The shift won in N trips or fewer. */
  | { readonly kind: 'withinTrips'; readonly trips: number };

export interface LevelDef {
  readonly id: string;
  readonly name: string;
  /** Why this level exists, straight from the roadmap's prototype level table. */
  readonly purpose: string;
  readonly floors: number;
  /**
   * Structural rules of the building this level sits in, resolved from the
   * building's id when the level is parsed. Absent means a plain tower.
   */
  readonly building?: BuildingRules;
  /** Which building it was, by name, so the level list can group by place. */
  readonly buildingId?: string;
  /**
   * The building's own lift (ADR-0027), resolved from the building when the
   * level is parsed. `elevator` already stands on its stock car; a loadout is
   * read in its parts. Absent means the standard lift.
   */
  readonly lift?: Lift;
  /** What a clear here pays, by stars: the building's own table, or the standard one. */
  readonly payout?: Readonly<Record<number, number>>;
  readonly elevator: ElevatorSpec;
  /**
   * More cars in the same bank (ADR-0034), each one the level's own car with
   * the player's loadout, starting at its own floor. The player drives every
   * one. Absent means one car, as on every level before it.
   */
  readonly extraCars?: readonly { readonly startFloor: FloorIndex }[];
  readonly spawns: readonly SpawnDef[];
  readonly objective: Objective;
  /** Safety valve so a run cannot last forever. Exceeding it ends the run as `lost`. */
  readonly timeLimitMs: number;
  readonly stars: StarThresholds;
  /**
   * The lift this level was balanced against, and what its star thresholds mean.
   *
   * Upgrades should make three stars easier, never automatic — see ADR-0011. A
   * level authored for a mid-tier lift still asks something of a player who has
   * one. Absent means the stock cabin.
   */
  readonly parLoadout?: Loadout;
  /**
   * Where the star thresholds came from. Tuning metadata, ignored by the sim.
   *
   * Human sessions beat bot runs, because bots have no reaction time — so once a
   * level has been calibrated against real play, `sweep --apply` must leave it
   * alone. Without this marker a routine recalibration silently replaces the
   * better numbers with the worse ones, which is exactly what happened during
   * v0.4 to levels 1-9.
   */
  readonly starsFrom?: 'humans' | 'bots';
  /**
   * Tuning metadata, ignored by the simulation. Set false on teaching levels
   * that are meant to be flat, so the difficulty sweep does not cry wolf about
   * a level with no optimization pressure by design. Defaults to true.
   */
  readonly expectsPressure?: boolean;
  /**
   * A hard shift (ADR-0037): announced as one before it starts, and paid half
   * as much again. The sim ignores it; content folds the pay into `payout`.
   */
  readonly hard?: boolean;
  /** The shift's bonus goal (ADR-0040). The sim judges it at the end and hashes nothing of it. */
  readonly bonus?: BonusGoal;
  /**
   * A puzzle's plan (ADR-0037): the floors of the fewest trips that work, in
   * order, as `solveTrips` finds them (`pnpm puzzles --apply`). Words for the
   * player after a loss, never read by a rule, and hashed nowhere.
   */
  readonly plan?: readonly number[];
}

/** Baseline cabin for the v0.1 prototype: 4 seats, no upgrades anywhere. */
export const V01_ELEVATOR: ElevatorSpec = {
  capacity: 4,
  msPerFloor: 800,
  doorOpenMs: 300,
  doorCloseMs: 300,
  transferMs: 220,
  minDwellMs: 250,
  maxDwellMs: 6000,
  startFloor: 0,
  longRunSavingPermille: 0,
};

/** The lift a level is played on: its building's own, or the standard one. */
export function liftOf(level: LevelDef): Lift {
  return level.lift ?? STANDARD_LIFT;
}

/**
 * The parts a shift is played with: the player's own, except on a puzzle, which
 * is played on the building's first lift (ADR-0049). A trip puzzle is planned
 * and graded on four seats (`solveTrips`); with eight it was over in six trips
 * of nine, against a plan of seven, and the owner asked why it was suddenly
 * short and easy (PLAYTEST_LOG, 2026-10-28).
 */
export function loadoutForShift(level: LevelDef, owned: Loadout): Loadout {
  return level.objective.kind === 'withinTrips' ? BASE_LOADOUT : owned;
}

/**
 * The same level on the standard lift, as every run before ADR-0027 was played.
 *
 * A building with a lift of its own sets the level's stock numbers from it, and
 * a level there may not set them itself (content enforces that), so putting the
 * standard stock back is exactly the level as it stood before. Old recorded
 * sessions replay on this and keep reproducing.
 */
export function onStandardLift(level: LevelDef): LevelDef {
  if (!level.lift) return level;
  const { lift: _ownLift, ...rest } = level;
  return { ...rest, elevator: { ...level.elevator, ...stockSpec(STANDARD_LIFT.parts) } };
}
